import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Network } from "vis-network";
import { DataSet } from "vis-data";
import Button from './ui/button';
import Input from './ui/input';
import { FaSearch, FaExpand, FaCompress, FaFilter, FaCog } from 'react-icons/fa';
import './MobileGraphView.css';

/**
 * Version mobile optimisée du graphique de nœuds et relations
 * Interface tactile avec contrôles simplifiés et navigation adaptée
 */
const MobileGraphView = ({
  nodes,
  relationships,
  onNodeSelect,
  selectedNode,
  onGraphRefresh,
  compact = false,
  readOnly = false // Nouveau prop pour mode lecture seule
}) => {
  const { t } = useTranslation();
  const graphRef = useRef(null);
  const networkRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [graphData, setGraphData] = useState({ nodes: [], edges: [] });
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedNodes, setHighlightedNodes] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [filterSettings, setFilterSettings] = useState({
    showLabels: true,
    nodeSize: 'medium',
    layoutType: 'physics'
  });

  // Configuration mobile optimisée pour vis-network (COPIÉE DU VRAI GRAPHVIEW)
  const getMobileNetworkOptions = () => ({
    layout: {
      hierarchical: false,
    },
    physics: {
      enabled: filterSettings.layoutType === 'physics',
      solver: "forceAtlas2Based",
      forceAtlas2Based: {
        gravitationalConstant: -50,
        centralGravity: 0.001,
        springLength: 150,
        springConstant: 0.08,
      },
      minVelocity: 0.75,
      stabilization: { iterations: 150 }
    },
    interaction: {
      hover: true,
      tooltipDelay: 300,
      dragNodes: true,
      dragView: true,
      zoomView: true,
      multiselect: false
    },
    nodes: {
      shape: "box",
      margin: 10,
      font: {
        size: compact ? 12 : 14,
        color: '#374151'
      },
      borderWidth: 2,
      borderWidthSelected: 4,
      shadow: {
        enabled: true,
        color: 'rgba(0,0,0,0.1)',
        size: 3,
        x: 1,
        y: 1
      }
    },
    edges: {
      width: 2, // Taille normale comme le vrai graphique
      color: {
        color: '#9ca3af', // Gris normal
        highlight: '#4f46e5', // Bleu primary pour highlight
        hover: '#4f46e5'
      },
      smooth: {
        enabled: true,
        type: 'dynamic',
        roundness: 0.5
      },
      arrows: {
        to: {
          enabled: true,
          scaleFactor: 1.2 // Comme le vrai graphique
        }
      },
      font: {
        size: compact ? 10 : 12,
        color: '#4b5563',
        strokeWidth: 1,
        strokeColor: '#ffffff'
      }
    }
  });

  // Fonction pour transformer les données en format vis-network
  const transformDataForGraph = () => {
    // Transformation des nœuds - COPIÉ DU VRAI GRAPHVIEW
    const visNodes = nodes.map(node => {
      const color = getNodeColor(node.type);
      const isHighlighted = highlightedNodes.includes(node.name); // Utiliser node.name comme ID !
      const isSelected = selectedNode?.name === node.name; // Comparer par name aussi !
      
      return {
        id: node.name, // ID = name, comme dans le vrai graphique !
        label: filterSettings.showLabels ? node.name : '',
        title: `${node.name}\nType: ${node.type}\nDescription: ${node.description || 'Aucune description'}`,
        color: {
          background: isSelected ? '#eef2ff' : isHighlighted ? '#fef3c7' : color.background,
          border: isSelected ? '#4f46e5' : isHighlighted ? '#f59e0b' : color.border
        },
        data: node
      };
    });

    // Transformation des relations - COPIÉ DU VRAI GRAPHVIEW
    const visEdges = relationships.map(rel => ({
      from: rel.nodeName1,
      to: rel.nodeName2,
      label: rel.relationshipType,
      title: `${rel.nodeName1} → ${rel.nodeName2}\nType: ${rel.relationshipType}`,
      arrows: {
        to: { enabled: true, scaleFactor: 1.2 }, // Exactement comme le vrai graphique
      }
    }));

    return { 
      nodes: new DataSet(visNodes), // Retour aux DataSet comme le vrai graphique
      edges: new DataSet(visEdges) 
    };
  };

  // Fonction pour obtenir la couleur d'un nœud selon son type (COPIÉ DU VRAI GRAPHVIEW)
  const getNodeColor = (type) => {
    switch (type) {
      case "Feature":
        return { background: "#4CAF50", border: "#2E7D32" };
      case "Feature Team":
        return { background: "#f078d6", border: "#9c58db" };
      case "Component":
        return { background: "#2196F3", border: "#1565C0" };
      case "API":
        return { background: "#FFC107", border: "#FFA000" };
      case "Service":
        return { background: "#9C27B0", border: "#7B1FA2" };
      case "User":
        return { background: "#F44336", border: "#C62828" };
      case "Module":
        return { background: "#3F51B5", border: "#303F9F" };
      default:
        return { background: "#78909C", border: "#455A64" };
    }
  };

  // Highlight relationships dynamically based on selected node
  const highlightEdges = (edges, selectedNode) => {
    return edges.map(edge => {
      const isRelated = edge.from === selectedNode?.name || edge.to === selectedNode?.name;
      return {
        ...edge,
        color: isRelated ? { color: '#4f46e5', highlight: '#4f46e5' } : edge.color,
        width: isRelated ? 3 : 2,
      };
    });
  };

  // Initialisation du graphique
  useEffect(() => {
    if (!graphRef.current || nodes.length === 0) return;

    const data = transformDataForGraph();
    const options = getMobileNetworkOptions();

    // Création ou mise à jour du réseau
    if (networkRef.current) {
      networkRef.current.setData(data);
      networkRef.current.setOptions(options);
    } else {
      networkRef.current = new Network(graphRef.current, data, options);
      
      // Forcer le redraw après l'initialisation pour s'assurer que les flèches sont visibles
      setTimeout(() => {
        if (networkRef.current) {
          networkRef.current.redraw();
          networkRef.current.fit();
        }
      }, 100);
      
      // Événements tactiles optimisés pour mobile
      networkRef.current.on('click', (params) => {
        if (params.nodes.length > 0) {
          const nodeName = params.nodes[0]; // C'est maintenant le nom du nœud
          const nodeData = nodes.find(n => n.name === nodeName); // Chercher par nom
          if (nodeData && onNodeSelect) {
            onNodeSelect(nodeData);
          }
        }
      });

      // Double tap pour zoom
      let lastTap = 0;
      networkRef.current.on('click', (params) => {
        const currentTime = new Date().getTime();
        const tapLength = currentTime - lastTap;
        if (tapLength < 500 && tapLength > 0) {
          // Double tap détecté
          if (params.nodes.length > 0) {
            networkRef.current.focus(params.nodes[0], { scale: 1.5 });
          } else {
            networkRef.current.fit();
          }
        }
        lastTap = currentTime;
      });
    }

    setGraphData(data);
  }, [nodes, relationships, selectedNode, filterSettings, highlightedNodes]);

  // Update graph data only when necessary
  useEffect(() => {
    if (!graphRef.current || nodes.length === 0) return;

    const data = transformDataForGraph();
    const updatedEdges = highlightEdges(data.edges, selectedNode);
    const updatedData = { ...data, edges: new DataSet(updatedEdges) };

    if (networkRef.current) {
      networkRef.current.setData(updatedData);
    } else {
      networkRef.current = new Network(graphRef.current, updatedData, getMobileNetworkOptions());
      networkRef.current.on('click', (params) => {
        if (params.nodes.length > 0) {
          const nodeName = params.nodes[0];
          const nodeData = nodes.find(n => n.name === nodeName);
          if (nodeData && onNodeSelect) {
            onNodeSelect(nodeData);
          }
        }
      });
    }

    setGraphData(updatedData);
  }, [nodes, relationships, selectedNode, filterSettings, highlightedNodes]);

  // Fonction de recherche
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setHighlightedNodes([]);
      return;
    }

    const matches = nodes.filter(node =>
      node.name.toLowerCase().includes(query.toLowerCase()) ||
      (node.description && node.description.toLowerCase().includes(query.toLowerCase())) ||
      node.type.toLowerCase().includes(query.toLowerCase())
    ).map(node => node.name); // Utiliser les noms

    setHighlightedNodes(matches);

    // Focus sur les nœuds trouvés
    if (matches.length > 0 && networkRef.current) {
      networkRef.current.focus(matches[0], { scale: 1.2 });
    }
  };

  // Basculer le mode plein écran
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Contrôles de zoom et navigation
  const handleZoomIn = () => {
    if (networkRef.current) {
      const scale = networkRef.current.getScale() * 1.5;
      networkRef.current.moveTo({ scale });
    }
  };

  const handleZoomOut = () => {
    if (networkRef.current) {
      const scale = networkRef.current.getScale() * 0.67;
      networkRef.current.moveTo({ scale });
    }
  };

  const handleFitView = () => {
    if (networkRef.current) {
      networkRef.current.fit({ animation: true });
    }
  };

  // Disable editing capabilities for mobile view
  useEffect(() => {
    if (networkRef.current) {
      networkRef.current.off('doubleClick'); // Remove double-click editing
      networkRef.current.off('oncontext'); // Remove context menu for editing
    }
  }, []);

  // Ensure no edit buttons are rendered in mobile controls
  const renderMobileControls = () => (
    <div className="mobile-graph-controls">
      <div className="mobile-graph-search">
        <div className="search-input-wrapper">
          <FaSearch style={{ color: '#718096', marginRight: '0.5rem' }} />
          <Input
            type="text"
            placeholder={t('mobileGraph.searchNodes')}
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="mobile-search-input"
          />
        </div>
      </div>

      <div className="mobile-graph-buttons">
        <Button
          onClick={handleZoomIn}
          className="mobile-graph-btn"
          title={t('mobileGraph.zoomIn')}
        >
          +
        </Button>
        <Button
          onClick={handleZoomOut}
          className="mobile-graph-btn"
          title={t('mobileGraph.zoomOut')}
        >
          -
        </Button>
        <Button
          onClick={handleFitView}
          className="mobile-graph-btn"
          title={t('mobileGraph.fitView')}
        >
          ⌂
        </Button>
        <Button
          onClick={toggleFullscreen}
          className="mobile-graph-btn"
          title={t('mobileGraph.fullscreen')}
        >
          {isFullscreen ? <FaCompress /> : <FaExpand />}
        </Button>
      </div>
    </div>
  );

  // Rendu des statistiques
  const renderStats = () => (
    <div className="mobile-graph-stats">
      <div className="stat-item">
        <span className="stat-value">{nodes.length}</span>
        <span className="stat-label">{t('mobileGraph.nodes')}</span>
      </div>
      <div className="stat-item">
        <span className="stat-value">{relationships.length}</span>
        <span className="stat-label">{t('mobileGraph.relations')}</span>
      </div>
      {highlightedNodes.length > 0 && (
        <div className="stat-item highlight">
          <span className="stat-value">{highlightedNodes.length}</span>
          <span className="stat-label">{t('mobileGraph.found')}</span>
        </div>
      )}
    </div>
  );

 

  return (
    <div className={`mobile-graph-container ${isFullscreen ? 'fullscreen' : ''} ${compact ? 'compact' : ''}`}>
      {!compact && renderMobileControls()}
      {!compact && renderStats()}
      
      
      <div className="mobile-graph-wrapper">
        <div 
          ref={graphRef} 
          className="mobile-graph-canvas"
          style={{ 
            height: compact ? '300px' : isFullscreen ? '100vh' : '400px',
            width: '100%'
          }}
        />
        
        {loading && (
          <div className="mobile-graph-loading">
            <div className="loading-spinner"></div>
            <span>{t('mobileGraph.loading')}</span>
          </div>
        )}
        
        {nodes.length === 0 && !loading && (
          <div className="mobile-graph-empty">
            <span className="empty-icon">📊</span>
            <p>{t('mobileGraph.noData')}</p>
            <small>{t('mobileGraph.createNodesFirst')}</small>
          </div>
        )}
      </div>

      {/* Affichage des informations du nœud sélectionné */}
      {selectedNode && (
        <div className="mobile-selected-node-info">
          <div className="selected-node-header">
            <h4>{selectedNode.name}</h4>
            <span className={`node-type-badge ${selectedNode.type?.toLowerCase()}`}>
              {selectedNode.type}
            </span>
          </div>
          <p className="selected-node-description">{selectedNode.description}</p>
          <div className="selected-node-meta">
            <span className="meta-item">
              <strong>Statut:</strong> {selectedNode.status}
            </span>
            {selectedNode.technology && (
              <span className="meta-item">
                <strong>Tech:</strong> {selectedNode.technology}
              </span>
            )}
            {/* Affichage des relations */}
            {relationships && relationships.length > 0 && (
              <span className="meta-item">
                <strong>Relations:</strong> {
                  relationships.filter(rel => 
                    rel.sourceNode === selectedNode._id || 
                    rel.targetNode === selectedNode._id ||
                    rel.nodeName1 === selectedNode.name ||
                    rel.nodeName2 === selectedNode.name
                  ).length
                } connectée(s)
              </span>
            )}
          </div>
        </div>
      )}

      {isFullscreen && (
        <Button
          onClick={toggleFullscreen}
          className="mobile-graph-exit-fullscreen"
        >
          <FaCompress /> {t('mobileGraph.exitFullscreen')}
        </Button>
      )}
    </div>
  );
};

export default MobileGraphView;
